import { apiClient } from '@/shared/lib/axios';
import { Insurer } from '@/types/admin-insurer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { InsurerFormData } from '@/schemas/insurerFormSchema';

export const createInsurer = async (
  siteType: string,
  data: InsurerFormData
): Promise<{ data: Insurer }> => {
  const response = await apiClient.post(`/admin/${siteType}/insurers`, data);
  return response.data;
};

export const updateInsurer = async (
  siteType: string,
  id: number,
  data: InsurerFormData
): Promise<{ data: Insurer }> => {
  const response = await apiClient.put(`/admin/${siteType}/insurers/${id}`, data);
  return response.data;
};

export const useCreateInsurerMutation = (siteType: string) => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: InsurerFormData) => createInsurer(siteType, data),
    onSuccess: async response => {
      toast.success('更新しました。');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['insurers', siteType] }),
        queryClient.invalidateQueries({ queryKey: ['insurerDetail', siteType, response.data.id] }),
      ]);
    },
    onError: (error) => {
      const status = (error as any)?.response?.status;
      if (status !== 401) {
        toast.error('エラーが発生しました。');
      }
    },
  });

  return { createInsurerMutation: mutateAsync, isPending };
};

export const useUpdateInsurerMutation = (siteType: string) => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: InsurerFormData }) =>
      updateInsurer(siteType, id, data),
    meta: { suppressNotFoundToast: true },
    onSuccess: async (_response, variables) => {
      toast.success('更新しました。');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['insurerDetail', siteType, variables.id] }),
        queryClient.invalidateQueries({ queryKey: ['insurers', siteType] }),
      ]);
    },
    onError: (error) => {
      const status = (error as any)?.response?.status;
      if (status !== 404 && status !== 401) {
        toast.error('エラーが発生しました。');
      }
    },
  });

  return { updateInsurerMutation: mutateAsync, isPending };
};
